import React, { useEffect } from 'react';

// ── LeakModal ──────────────────────────────────────────────────────────────
//
// Blurred-backdrop modal opened from the Home "Leak detected" badge. Explains
// what was detected, when (start/end if known), what to check, and offers a
// "View on chart" button that deep-links to Trends at the 48h range (leaks
// clear at 48h, so that range always shows the region).
//
// Content is driven by leak.kind:
//   'slow' / 'away' — ongoing slow loss + things to check
//   'rapid'         — a "usage alert": a drop over the customer's own setting
//                     (a leak, or just heavy use)

function fmtTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-US', {
    timeZone: 'Pacific/Honolulu',
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// Time without readings, e.g. "about 6 hours" / "40 minutes".
function fmtGap(ms) {
  const m = Math.round((ms || 0) / 60000);
  if (m >= 90) return `about ${Math.round(m / 60)} hours`;
  if (m >= 60) return 'about an hour';
  return `${m} minutes`;
}

function fmtWindow(min) {
  if (!min) return '';
  if (min % 1440 === 0) { const d = min / 1440; return `${d} day${d > 1 ? 's' : ''}`; }
  if (min % 60 === 0)   { const h = min / 60;   return `${h} hour${h > 1 ? 's' : ''}`; }
  return `${min} minutes`;
}

function IconWarn() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a1 1 0 0 0 .86 1.5h18.64a1 1 0 0 0 .86-1.5L13.71 3.86a1 1 0 0 0-1.72 0z" />
      <path d="M12 9v4" /><path d="M12 17h.01" />
    </svg>
  );
}

// A tank sitting at or above this percent is close enough to its Full mark that
// a recent fill past the overflow is a plausible explanation for the drop.
// Matches NEAR_FULL_PCT in the cloud's leak.js — keep the two in step.
const NEAR_FULL_PCT = 96;

export default function LeakModal({ open, onClose, onDismiss, leak, tankName, singleTank, onViewChart, onManageAlerts, irrigation, pct }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !leak) return null;

  // What to call the tank IN A SENTENCE. On a single-tank system the tank has
  // no name worth saying — it is just their tank, and reading the location name
  // mid-sentence ("We noticed Home slowly losing water") is strange. The heading
  // still shows the name; only the prose changes.
  const subject = singleTank ? 'your tank' : (tankName || 'this tank');
  const Subject = singleTank ? 'Your tank' : (tankName || 'This tank');

  const rapid = leak.kind === 'rapid';
  const away = leak.kind === 'away';
  // Rapid only: where the event stands — dropping / confirming / offline /
  // settled ('ended' = an older record that never got a settled entry).
  const rapidState = leak.state || (leak.endTs ? 'settled' : 'dropping');
  const inProgress = rapidState === 'dropping' || rapidState === 'confirming';
  const gal = Math.round(leak.observedGal ?? leak.gallons ?? 0);
  const title = rapid ? 'Usage alert' : (away ? 'Possible leak while away' : 'Possible slow leak');

  // Still sitting near the top. A tank filled past the overflow drains back down
  // to the port on its own, and once the head above the port is nearly gone that
  // can take a day or more — a real decline, but not a leak in the tank. We say
  // so rather than suppressing: the alert still stands, the reader decides.
  // Read from the tank's CURRENT level (not the level when the event fired),
  // because "still near full" is a statement about right now.
  const nearFull = typeof pct === 'number' && pct >= NEAR_FULL_PCT;
  const nearFullNote = nearFull ? (
    <p className="leak-modal-note">
      This tank is still near full. If it was recently filled to overflow, it can keep
      draining slowly through the overflow for a day or more — that's normal, and it
      should level off on its own. If it regularly reads above 100%, check the Full
      level in Settings.
    </p>
  ) : null;

  return (
    <div className="leak-modal-scrim" onClick={onClose}>
      <div className="leak-modal" role="dialog" aria-modal="true" aria-label={title}
        onClick={(e) => e.stopPropagation()}>
        <button type="button" className="leak-modal-close" aria-label="Close" onClick={onClose}>×</button>

        <div className="leak-modal-head">
          <span className="leak-modal-head__icon"><IconWarn /></span>
          <div>
            <div className="leak-modal-title">{title}</div>
            {tankName && <div className="leak-modal-sub">{tankName}</div>}
          </div>
        </div>

        {rapid ? (
          <div className="leak-modal-body">
            {/* What + how much + where it stands now. The figures are the same
                running total the texts carry (peak − lowest), and while it's in
                progress they update with every reading. "Leveled off" is only
                said once the cloud has seen 30 minutes with no drop at all. */}
            <p>
              {rapidState === 'settled' || rapidState === 'ended' ? (
                <>
                  {Subject} dropped about <strong>{gal} gallons</strong> from {fmtTime(leak.startTs)}.
                  {rapidState === 'ended'
                    ? ` Last recorded ${fmtTime(leak.endTs)}.`
                    : leak.endAfterGap
                      ? ` It had leveled off by ${fmtTime(leak.endTs)}.`
                      : ` It leveled off at ${fmtTime(leak.endTs)}.`}
                </>
              ) : rapidState === 'offline' ? (
                <>
                  {Subject} had dropped about <strong>{gal} gallons</strong> since{' '}
                  {fmtTime(leak.startTs)}. There have been no readings since{' '}
                  {fmtTime(leak.updatedTs)}, so we can&apos;t tell yet whether it has stopped.
                </>
              ) : rapidState === 'confirming' ? (
                <>
                  {Subject} has dropped about <strong>{gal} gallons</strong> since{' '}
                  {fmtTime(leak.startTs)}. It&apos;s leveling off, with no further drop
                  since {fmtTime(leak.lastDropTs)}.
                </>
              ) : (
                <>
                  {Subject} has dropped about <strong>{gal} gallons</strong> since{' '}
                  {fmtTime(leak.startTs)}, and it&apos;s still dropping.
                </>
              )}
            </p>
            {inProgress && leak.updatedTs ? (
              <p className="leak-modal-note">Updated {fmtTime(leak.updatedTs)}</p>
            ) : null}
            {leak.gapMs >= 15 * 60000 ? (
              <p className="leak-modal-note">
                Includes {fmtGap(leak.gapMs)} without readings from the device.
              </p>
            ) : null}
            {/* Why / how we know */}
            <p className="leak-modal-note">
              That&apos;s over your alert setting of {leak.gallons} gallons in{' '}
              {fmtWindow(leak.windowMinutes)}. It could be a leak, or just more use
              than usual. You can change this alert in Settings.
            </p>
            {/* Possible causes */}
            <p>Could be:</p>
            <ul className="leak-modal-list">
              <li>Heavy use (laundry, watering, filling something)</li>
              <li>A tap or hose left running</li>
              <li>A running toilet</li>
              <li>A leak or burst pipe</li>
            </ul>
            {nearFullNote}
          </div>
        ) : (
          <div className="leak-modal-body">
            {/* What + when */}
            <p>
              We noticed {subject} slowly losing water
              {away ? ' while Away mode was on' : ' overnight'}, starting {fmtTime(leak.startTs)}.
              {leak.endTs ? ` This settled around ${fmtTime(leak.endTs)}.` : ''}
            </p>
            {/* How much — the measured loss (running total while ongoing). */}
            {leak.observedGal ? (
              <p>
                {away
                  ? <>About <strong>{Math.round(leak.observedGal)} gallons</strong> lost{leak.endTs ? '' : ' so far'}.</>
                  : <>It ended about <strong>{Math.round(leak.observedGal)} gallons</strong> lower than it started.</>}
              </p>
            ) : null}
            {/* Why / how we know. Only for the overnight case: the away branch
                said the same thing the opening sentence already said. */}
            {!away && (
              <p className="leak-modal-note">
                You&apos;re seeing this because the level kept falling overnight,
                across quiet hours with no water use.
              </p>
            )}
            {/* Possible causes */}
            <p>Common causes worth checking:</p>
            <ul className="leak-modal-list">
              <li>A running or leaking toilet</li>
              <li>A dripping faucet or shower</li>
              <li>A hose or spigot left dripping</li>
              <li>Any other slow leak in the line</li>
              {away && <li>An irrigation cycle using more water than expected</li>}
            </ul>
            {/* Away irrigation reminder — mirrors the email. On → estimate may be
                too short; off → prompt to set it so watering isn't flagged. */}
            {away && (
              irrigation && irrigation.on === true ? (
                <p className="leak-modal-note">
                  We only flag drops lasting longer than your estimated{' '}
                  {irrigation.estMinutes || 60}-minute irrigation run time. If a cycle
                  ran longer than that, it may explain this.
                </p>
              ) : (
                <p className="leak-modal-note">
                  Run irrigation on a timer? Turn on the irrigation setting under Away
                  mode so scheduled watering isn&apos;t flagged as a leak.{' '}
                  {onManageAlerts && (
                    <button type="button" className="leak-modal-link"
                      onClick={() => onManageAlerts('irrigation')}>
                      Set irrigation time
                    </button>
                  )}
                </p>
              )
            )}
            {/* The one explanation the list above can't offer. Every other cause
                here is plumbing; if the house is occupied, the answer is that
                Away mode is on and ordinary use looks like a leak to it. */}
            {away && (
              <p className="leak-modal-note">
                <strong>Away mode is on.</strong> If anyone is home and using water,
                normal use can look like a slow leak.{' '}
                {onManageAlerts && (
                  <button type="button" className="leak-modal-link"
                    onClick={() => onManageAlerts('away')}>
                    Change alert settings
                  </button>
                )}
              </p>
            )}
            {nearFullNote}
            {!leak.endTs && <p>Keep an eye on it — we'll let you know if it continues.</p>}
          </div>
        )}

        <div className="leak-modal-actions">
          <button type="button" className="btn btn-primary" onClick={onViewChart}>View on chart</button>
          {/* Only a SETTLED event can be dismissed — while it's still ongoing we
              keep it as a plain Close so an active leak can't be cleared. The top
              "×" always just closes. Dismiss hides only this dashboard badge;
              Trends and the sent alert are untouched. */}
          {leak.settled && onDismiss ? (
            <button type="button" className="btn btn-secondary" onClick={onDismiss}>Dismiss alert</button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}
